import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Funnel, ArrowRight, ShieldCheck, GraduationCap } from "@phosphor-icons/react";
import Seo from "@/components/Seo";

// US state two-letter to display name for label readability
const STATE_NAMES = {
  CA: "California", DE: "Delaware", FL: "Florida", IA: "Iowa",
  ID: "Idaho", MO: "Missouri", OH: "Ohio", OR: "Oregon",
  TN: "Tennessee", TX: "Texas", WA: "Washington", WY: "Wyoming",
};

const CATEGORY_ORDER = ["State Permit", "Fundamentals", "Carry & Legal", "Safety & Storage"];

export default function Elearning() {
  const [courses, setCourses] = useState([]);
  const [cat, setCat] = useState("ALL");
  const [state, setState] = useState("ALL");

  useEffect(() => {
    api.get("/elearning/courses").then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  const categories = useMemo(
    () => ["ALL", ...CATEGORY_ORDER.filter(c => courses.some(x => x.category === c))],
    [courses]
  );
  const states = useMemo(
    () => ["ALL", ...Array.from(new Set(courses.filter(c => c.state).map(c => c.state))).sort()],
    [courses]
  );

  const filtered = useMemo(() => courses.filter(c =>
    (cat === "ALL" || c.category === cat) &&
    (state === "ALL" || c.state === state)
  ), [courses, cat, state]);

  return (
    <div data-testid="elearning-page">
      <Seo
        title="eLearning — U.S. LawShield Online Firearm Training"
        description="27 self-paced online firearm courses sponsored by U.S. LawShield: state CCW/HQPP permit training, handgun fundamentals, carry law, range safety, and stop-the-bleed essentials."
        path="/elearning"
      />
      {/* Sponsored banner */}
      <section className="border-b border-white/10 pt-24 pb-10 bg-gradient-to-b from-[#FFD300]/[0.05] to-transparent">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-3xl">
              <div className="overline mb-4 flex items-center gap-2">
                <ShieldCheck size={14} weight="duotone" className="text-[#FFD300]" />
                SPONSORED BY <span className="text-[#FFD300]">U.S. LAWSHIELD<sup>®</sup></span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.92]">
                Online Firearm Training.<br/>
                <span className="text-[#FFD300]">State-compliant. On your schedule.</span>
              </h1>
              <p className="mt-6 text-neutral-400 leading-relaxed text-base max-w-2xl">
                Self-paced eLearning courses developed by Independent Program Attorneys and certified instructors —
                including state permit courses for {courses.filter(c => c.state).length} states,
                marksmanship, carry law, and stop-the-bleed essentials.
              </p>
            </div>
            <div className="surface-card p-5 max-w-sm w-full" data-testid="lawshield-banner">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-[#FFD300] grid place-items-center text-black">
                  <GraduationCap size={22} weight="duotone" />
                </div>
                <div>
                  <div className="font-heading text-sm">Why these courses?</div>
                  <div className="text-xs text-neutral-400">From the leader in legal defense</div>
                </div>
              </div>
              <ul className="text-xs text-neutral-300 space-y-1.5">
                <li>• State-compliant curriculum</li>
                <li>• Lifetime access — any device</li>
                <li>• Certificate of completion</li>
                <li>• Built with program attorneys</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="section-pad">
        <div className="container-x grid grid-cols-12 gap-8">
          <aside className="col-span-12 lg:col-span-3" data-testid="elearning-filters">
            <div className="sticky top-24 surface-card p-6">
              <div className="flex items-center gap-2 mb-6">
                <Funnel size={16} weight="duotone" className="text-[#FFD300]" />
                <div className="overline">Filter</div>
              </div>

              <div className="mb-6">
                <div className="label-mono mb-3">Category</div>
                <div className="space-y-2">
                  {categories.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCat(c)}
                      data-testid={`elearning-filter-cat-${c.replace(/[\s&]/g, '-')}`}
                      className={`block w-full text-left font-mono text-xs uppercase tracking-[0.15em] py-2 px-3 transition-colors ${cat === c ? 'bg-[#FFD300] text-black' : 'hover:bg-white/5 text-neutral-300'}`}
                    >{c}</button>
                  ))}
                </div>
              </div>

              <div>
                <div className="label-mono mb-3">State Permit</div>
                <div className="grid grid-cols-3 gap-2">
                  {states.map((s) => (
                    <button
                      key={s}
                      onClick={() => setState(s)}
                      data-testid={`elearning-filter-state-${s}`}
                      className={`state-tag py-2 transition-colors ${state === s ? '!bg-[#FFD300] !text-black !border-[#FFD300]' : 'hover:bg-white/10'}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <div className="col-span-12 lg:col-span-9" data-testid="elearning-grid">
            <div className="overline mb-6">{filtered.length} COURSES AVAILABLE</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
              {filtered.map((c) => (
                <Link
                  key={c.slug}
                  to={`/elearning/${c.slug}`}
                  className="bg-[#0A0A0A] p-6 lift block group"
                  data-testid={`elearning-card-${c.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden mb-5 bg-[#0E0E0E] relative">
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
                    <div className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.2em] uppercase bg-[#FFD300] text-black px-2 py-1">SPONSORED</div>
                    {c.state && (
                      <div className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.18em] uppercase bg-black/70 backdrop-blur text-[#FFD300] px-2 py-1 border border-[#FFD300]/40">
                        {STATE_NAMES[c.state] || c.state}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="code-badge">{c.category.toUpperCase()}</span>
                    {c.state && <span className="state-tag">{c.state}</span>}
                  </div>
                  <h3 className="font-heading text-xl tracking-tight leading-tight mb-3">{c.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed mb-5 line-clamp-3">{c.short_description}</p>
                  <div className="flex items-center justify-between border-t border-white/10 pt-4">
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">U.S. LawShield®</div>
                    <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#FFD300] group-hover:translate-x-1 transition-transform flex items-center gap-1">
                      View Course <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              ))}
              {filtered.length === 0 && (
                <div className="col-span-full bg-black p-12 text-center text-neutral-500 font-mono text-sm">No courses match your filters.</div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
